import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, createRef, useRef } from 'react';
import FormContainer from "../../../components/FormContainer";
import Barang from '../../../models/Barang';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { PageContainerActionParamType } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { DataFormFieldChangeParamType, SaveDataParamType } from '../../../utils/Types';
import Satuan from '../../../models/Satuan';
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';
import { validate as uuidValidate } from "uuid";
import KategoriBrg from '../../../models/KategoriBrg';
import GrupBarang from '../../../models/GroupBrg';

const groupName: MenuGroupName = "Stok";
const subgroupName: string = "Master";
const code: MenuCode = "STOCK-BARANG";
const title: MenuTitle = "Barang";
const endpoint = "brg";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState<Barang>({is_new: true});
	const satuanRef = useRef();
	const kategoriBrgRef = useRef();
	const groupBrgRef = useRef();
	const navigate = useNavigate();

    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
			return;

        setLoading(true);
		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => {
				const data: Barang | null = response.data?.data ? {
					...response.data?.data,
                    satuan: response.data.data.ucode_satuan ?
                        {
                            ucode: response.data.data.ucode_satuan,
                            kode: response.data.data.kode_satuan,
                            nama: response.data.data.nama_satuan,
                            aktif: response.data.data.aktif,
                            keterangan: response.data.data.keterangan,
                        }
                        :
                        undefined,
					list_kategori_brg: response.data.data.list_kategori_brg?.map((k: KategoriBrg) => ({
                            ucode: k.ucode,
                            kode: k.kode,
                            nama: k.nama,
                        }))
                        ??
                        [],
					list_group_brg: response.data.data.list_group_brg?.map((k: GrupBarang) => ({
							ucode: k.ucode,
							kode: k.kode,
							nama: k.nama,
						}))
						??
						[],
					is_new: false,
				} 
				: 
				null;
				setFormData(data? {...data} : {is_new: true});			
			})
            .catch((error) => console.error({error}))
            .finally(() => {
				setLoading(false);
			})
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onValidate = useCallback((data: Barang | undefined) => {
        if (!data?.nama)
            return false;

        if (!data?.is_new && !data?.kode)
            return false;

        if (!data?.ucode_satuan)
            return false;

        return true;
    }, [formData]);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {        
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<Barang> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const dataToSend = {
            ucode: param.data.ucode,
            kode: param.data.kode,
            nama: param.data.nama,
			is_non_stok: param.data.is_non_stok,
			aktif: param.data.aktif,
			keterangan: param.data.keterangan,
			ucode_satuan: param.data.ucode_satuan,
			list_kategori_brg: param.data.list_kategori_brg?.map(k => ({ucode: k.ucode})) ?? [],
			list_group_brg: param.data.list_group_brg?.map(k => ({ucode: k.ucode})) ?? [],
        };

        const apiCallerObj = param.data.ucode ? apiCaller.put(endpoint, JSON.stringify(dataToSend)) : apiCaller.post(endpoint, JSON.stringify(dataToSend));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, formData]);

    return (
		<>
        {
            loading && <Loader />
        }
			<FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
				<DataForm<Barang>
					groupName={groupName}
					subgroupName={subgroupName}
					code={code}
					title={title}
					data={formData}
					isViewMode={isViewMode}
					fields={[
						{
							name: "kode",
							title: "Kode Barang",
							inputType: "text",
							dataType: (typeof formData.kode),
							isReadonly: false,
							isRequired: formData?.ucode ? true : false,
							onChanged: (valueObj: DataFormFieldChangeParamType<Barang>) => onChanged(valueObj),
						},
						{
							name: "nama",
							title: "Nama Barang",
							inputType: "text",
							dataType: (typeof formData.nama),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Barang>) => onChanged(valueObj),
						},
						{
							name: "kode_lain",
							title: "Kode Lain",
							inputType: "text",
							dataType: (typeof formData.kode_lain),
							isReadonly: false,
							isRequired: false,
							onChanged: (valueObj: DataFormFieldChangeParamType<Barang>) => onChanged(valueObj),
						},
						{
							name: "nama_lain",
							title: "Nama Lain",
							inputType: "text",
							dataType: (typeof formData.nama_lain),
							isReadonly: false,
							isRequired: false,
							onChanged: (valueObj: DataFormFieldChangeParamType<Barang>) => onChanged(valueObj),
						},
						{
							name: "is_non_stok",
							title: "Non Stok",
							inputType: "checkbox",
							dataType: (typeof formData.is_non_stok),
							isReadonly: false,
							isRequired: false,
							onChanged: (valueObj: DataFormFieldChangeParamType<Barang>) => onChanged(valueObj),
						},
						{
							name: "keterangan",
							title: "Keterangan",
							inputType: "textarea",
							dataType: (typeof formData.keterangan),
							isReadonly: false,
							isRequired: false,
							onChanged: (valueObj: DataFormFieldChangeParamType<Barang>) => onChanged(valueObj),
							rows: 3,
						},
						{
							name: "satuan",
							keyField: "ucode",
							labelField: "nama",
							editKeyField: "ucode_satuan",
							title: "Satuan",
							inputType: "combobox",
							dataType: (typeof Satuan),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Barang>) => onChanged(valueObj),
							searchEndpoint: "satuan/nama",
							searchEndpointFilterParamName: "nama",
							ref: satuanRef,
						},
						{
							name: "list_kategori_brg",
							keyField: "ucode",
							labelField: "nama",
							editKeyField: "list_kategori_brg",
							title: "List Kategori Barang",
							inputType: "multiselect",
							dataType: (typeof KategoriBrg),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Barang>) => onChanged(valueObj),
							searchEndpoint: "kategoribrg/nama",
							searchEndpointFilterParamName: "nama",
							ref: kategoriBrgRef,
						},
						{
							name: "list_group_brg",
							keyField: "ucode",
							labelField: "nama",
							editKeyField: "list_group_brg",
							title: "List Grup Barang",
							inputType: "multiselect",
							dataType: (typeof GrupBarang),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Barang>) => onChanged(valueObj),
							searchEndpoint: "groupbrg/nama",
							searchEndpointFilterParamName: "nama",
							ref: groupBrgRef,
						},					
					]}
					onValidate={onValidate}
					onClose={(pathObject) => onClose(pathObject)}
					onSave={(data) => onSave(data)}
				/>
			</FormContainer>
		</>
    );

};

export default Form;
